import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  TOUCH,
  useDebouncedValue,
} from "@nymbl/nos2-rules";
import { ChevronRight, Link, Star } from "lucide-react";
import { useMemo, useState } from "react";
import { TypeChip } from "@/components/time/TypeChip";
import {
  FAVORITES_LIMIT,
  useAssignable,
  useAssignableProjects,
  useProjectTasks,
  useSetFavorite,
  useSetProjectFavorite,
} from "@/hooks/use-time-assignable";
import type { TimeEntryType } from "@/lib/enums";
import { copyLinkLabel, favoriteLabel } from "@/lib/time/labels";
import { groupByProject } from "@/lib/time/table-view";
import { copyTaskLink } from "@/lib/time/task-link";
import { cn } from "@/lib/utils";
import type { AssignableTask } from "@/schemas/time";

interface FavoritesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The parent remounts the dialog with a new `key` per opening so the search starts fresh. */
  initialQuery: string;
}

/** What a project heading shows and its star does, from the project as the window sees it. */
interface ProjectHead {
  projectId: string | null;
  name: string;
  accountName: string | null;
  type: TimeEntryType | null;
  taskCount: number;
  starredCount: number;
  projectIsFavorite: boolean;
}

/** "Crye SOW #6 (Crye)": the project in a control's name, with its client when there is one. */
function projectName(p: ProjectHead): string {
  return p.accountName ? `${p.name} (${p.accountName})` : p.name;
}

/**
 * The heading's star: Star project (every task, later ones too), Project starred (click to remove
 * it), or Star all when the project is starred but some tasks were taken off it.
 */
function projectStar(p: ProjectHead) {
  if (p.projectIsFavorite && p.starredCount >= p.taskCount) {
    return {
      on: true,
      half: false,
      text: "Project starred",
      label: `Project starred, ${projectName(p)}`,
    };
  }
  return {
    on: false,
    half: p.projectIsFavorite,
    text: p.projectIsFavorite ? "Star all" : "Star project",
    label: p.projectIsFavorite ? `Star all in ${projectName(p)}` : `Star project ${projectName(p)}`,
  };
}

function countLine(p: ProjectHead): string {
  const count =
    p.starredCount > 0
      ? `${p.starredCount} of ${p.taskCount} starred`
      : `${p.taskCount} ${p.taskCount === 1 ? "task" : "tasks"}`;
  return [p.accountName, count].filter(Boolean).join(" · ");
}

/** A project's heading: its name and counts (a toggle when `onToggle` is given), type and star. */
function ProjectHeading({
  project,
  expanded,
  onToggle,
  busy,
  onStar,
}: {
  project: ProjectHead;
  expanded?: boolean;
  onToggle?: () => void;
  busy: boolean;
  onStar: (projectId: string, isFavorite: boolean) => void;
}) {
  const star = projectStar(project);
  const title = (
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold break-words">{project.name}</span>
      <span className="block text-xs break-words text-muted-foreground">{countLine(project)}</span>
    </span>
  );
  return (
    <div className="sticky top-0 z-10 flex items-center gap-2 border-b bg-muted/60 py-1 pr-1 pl-2 backdrop-blur-sm">
      {onToggle ? (
        <Button
          variant="ghost"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={`${expanded ? "Hide" : "Show"} tasks in ${projectName(project)}`}
          className={cn(
            TOUCH,
            "h-auto min-w-0 flex-1 justify-start whitespace-normal text-left font-normal",
          )}
        >
          <ChevronRight
            className={cn(
              "size-4 shrink-0 text-muted-foreground transition-transform",
              expanded && "rotate-90",
            )}
            aria-hidden="true"
          />
          {title}
        </Button>
      ) : (
        <span className="flex min-h-11 min-w-0 flex-1 items-center pl-1">{title}</span>
      )}
      {project.type && (
        <span className="max-sm:hidden">
          <TypeChip type={project.type} />
        </span>
      )}
      {project.projectId && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(TOUCH, "shrink-0 cursor-pointer bg-background")}
          aria-pressed={star.on}
          aria-label={star.label}
          disabled={busy}
          onClick={() => {
            if (project.projectId) onStar(project.projectId, !star.on);
          }}
        >
          <Star
            className={cn(
              "size-4",
              star.on
                ? "fill-primary text-primary"
                : star.half
                  ? "fill-primary/30 text-primary"
                  : "text-muted-foreground",
            )}
            aria-hidden="true"
          />
          <span className="max-sm:sr-only">{star.text}</span>
        </Button>
      )}
    </div>
  );
}

/** The tasks under a project heading, each with its own star. */
function TaskRows({
  tasks,
  busy,
  onStar,
}: {
  tasks: AssignableTask[];
  busy: boolean;
  onStar: (task: AssignableTask) => void;
}) {
  return (
    <div className="divide-y border-b">
      {tasks.map((t) => (
        <div
          key={t.projectTaskId}
          className="flex min-h-12 items-center justify-between gap-3 py-1 pr-1 pl-8 hover:bg-muted/50"
        >
          <span className="min-w-0 flex-1 text-sm font-semibold break-words">{t.taskName}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(TOUCH, "shrink-0 cursor-pointer text-muted-foreground")}
            aria-label={copyLinkLabel(t)}
            title="Copy link"
            onClick={() => void copyTaskLink(t.projectTaskId)}
          >
            <Link className="size-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(TOUCH, "shrink-0 cursor-pointer")}
            aria-pressed={t.isFavorite}
            aria-label={favoriteLabel(t, t.isFavorite)}
            disabled={busy}
            onClick={() => onStar(t)}
          >
            <Star
              className={cn(
                "size-5",
                t.isFavorite ? "fill-primary text-primary" : "text-muted-foreground",
              )}
              aria-hidden="true"
            />
          </Button>
        </div>
      ))}
    </div>
  );
}

function Loading() {
  return (
    <div className="space-y-1 p-1" aria-busy="true">
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
    </div>
  );
}

/** One project's tasks, loaded when it is opened. */
function ProjectTasks({
  projectId,
  busy,
  onStar,
}: {
  projectId: string;
  busy: boolean;
  onStar: (task: AssignableTask) => void;
}) {
  const tasks = useProjectTasks(projectId, true);
  if (tasks.isError) {
    return (
      <p role="alert" className="px-3 py-3 text-sm text-danger">
        {tasks.error.message}
      </p>
    );
  }
  if (tasks.isPending) return <Loading />;
  const seen = new Set<string>();
  const unique = tasks.data.items.filter(
    (t) => !seen.has(t.projectTaskId) && seen.add(t.projectTaskId),
  );
  return (
    <TaskRows
      tasks={[...unique].sort((a, b) => a.taskName.localeCompare(b.taskName))}
      busy={busy}
      onStar={onStar}
    />
  );
}

/**
 * Favorite tasks, by project. With no search: every project you can log to, starred ones first;
 * open one to star its tasks, or star the whole project so its tasks (later ones too) are
 * favorites; a task can still be taken off a starred project. A search lists the matching tasks
 * under their projects.
 */
export function FavoritesDialog({ open, onOpenChange, initialQuery }: FavoritesDialogProps) {
  const [query, setQuery] = useState(initialQuery);
  const debounced = useDebouncedValue(query.trim(), 250);
  const browsing = debounced === "";
  const projects = useAssignableProjects(open && browsing);
  const byProject = browsing && projects.data?.supported === true;
  const search = useAssignable(debounced, FAVORITES_LIMIT);
  const favorite = useSetFavorite();
  const projectFavorite = useSetProjectFavorite();
  const busy = favorite.isPending || projectFavorite.isPending;
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  const items = search.data?.items;
  const total = search.data?.total ?? 0;
  const groups = useMemo(() => groupByProject(items ?? []), [items]);
  const shown = groups.reduce((n, g) => n + g.tasks.length, 0);

  const starTask = (t: AssignableTask) =>
    favorite.mutate({ projectTaskId: t.projectTaskId, isFavorite: !t.isFavorite });
  const starProject = (projectId: string, isFavorite: boolean) =>
    projectFavorite.mutate({ projectId, isFavorite });
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const error = byProject ? null : search.isError ? search.error.message : null;
  const pending = browsing
    ? projects.isPending || (!byProject && search.isPending)
    : search.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-sm:top-0 max-sm:left-0 max-sm:h-dvh max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none grid-rows-[auto_auto_minmax(0,1fr)_auto] sm:max-h-[90vh] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Favorite tasks</DialogTitle>
          <DialogDescription className="sr-only">
            Star a task or a whole project to keep it in your week grid; search to find more.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="fav-search" className="sr-only">
            Search your assigned tasks
          </Label>
          <Input
            id="fav-search"
            type="search"
            autoComplete="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search projects or tasks"
            className={TOUCH}
          />
        </div>
        <div className="flex min-h-0 flex-col gap-2">
          {projects.isError && browsing ? (
            <p role="alert" className="px-2 py-3 text-sm text-danger">
              {projects.error.message}
            </p>
          ) : error ? (
            <p role="alert" className="px-2 py-3 text-sm text-danger">
              {error}
            </p>
          ) : pending ? (
            <Loading />
          ) : byProject ? (
            <div className="min-h-0 overflow-y-auto rounded-md border">
              {projects.data?.projects.length === 0 && (
                <p className="px-3 py-3 text-sm text-muted-foreground">No projects</p>
              )}
              {projects.data?.projects.map((p) => {
                const open = expanded.has(p.projectId);
                return (
                  <section
                    key={p.projectId}
                    aria-label={
                      p.accountName ? `${p.projectName} (${p.accountName})` : p.projectName
                    }
                  >
                    <ProjectHeading
                      project={{
                        projectId: p.projectId,
                        name: p.projectName,
                        accountName: p.accountName,
                        type: p.type,
                        taskCount: p.taskCount,
                        starredCount: p.starredCount,
                        projectIsFavorite: p.projectIsFavorite,
                      }}
                      expanded={open}
                      onToggle={() => toggle(p.projectId)}
                      busy={busy}
                      onStar={starProject}
                    />
                    {open && <ProjectTasks projectId={p.projectId} busy={busy} onStar={starTask} />}
                  </section>
                );
              })}
            </div>
          ) : (
            <>
              <div
                className={cn(
                  "min-h-0 overflow-y-auto rounded-md border",
                  search.isPlaceholderData && "opacity-60",
                )}
              >
                {groups.length === 0 && (
                  <p className="px-3 py-3 text-sm text-muted-foreground">No matches</p>
                )}
                {groups.map((g) => (
                  <section
                    key={g.key}
                    aria-label={g.accountName ? `${g.name} (${g.accountName})` : g.name}
                  >
                    <ProjectHeading
                      project={{
                        projectId: g.projectId,
                        name: g.name,
                        accountName: g.accountName,
                        type: g.tasks[0]?.type ?? null,
                        taskCount: g.tasks.length,
                        starredCount: g.starred,
                        projectIsFavorite: g.projectIsFavorite,
                      }}
                      busy={busy}
                      onStar={starProject}
                    />
                    <TaskRows tasks={g.tasks} busy={busy} onStar={starTask} />
                  </section>
                ))}
              </div>
              {total > shown && (
                <p className="text-xs text-muted-foreground">
                  Showing {shown} of {total}
                </p>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button type="button" className={TOUCH} onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
